import { useApp } from 'app';
import LangIcons from 'app/components/LangIcons';
import useAxios from 'axios-hooks';
import { APP_NAME, CLIENT_ID, GRANT_TYPE, LANG_DEFAULT } from 'constant';
import { router } from 'constant/router';
import { Button, Form } from 'devextreme-react';
import { loadMessages, locale } from 'devextreme/localization';
import useDictionary from 'hooks/useDictionary';
import { logo } from 'images';
import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import tokenService from 'services/auth.service';
import { authenInfoAtom } from 'store/atom-state/authen-atom';
import { notification } from 'utils/notification';
import './styles.scss';
import LoadPanel from 'app/components/LoadPanel';

const eye = 'fas fa-eye';
const eyeSlash = 'fas fa-eye-slash';

export function Login() {
  const history = useHistory();
  const { t }: any = useDictionary({});
  const { setVoca }: any = useApp();
  const formRef: any = useRef(null);
  const [authenInfo, setAuthenInfo] = useRecoilState(authenInfoAtom);
  const isLoggedIn = authenInfo?.isLoggedIn;
  const [formData] = useState<any>({
    loginId: '',
    password: '',
  });
  const [viewPassword, setViewPassword] = useState({
    password: false,
    conPassword: false,
  });

  const [{ loading }, fetchData] = useAxios(
    {},
    { manual: true, autoCancel: true, useCache: false },
  );

  const handleSubmit = async () => {
    const isValid = formRef?.current?.instance?.validate()?.isValid;
    if (!isValid) return;
    let form_data = new FormData();
    form_data.append('client_id', CLIENT_ID);
    form_data.append('username', formData?.loginId?.toLowerCase?.()?.trim());
    form_data.append('password', formData?.password);
    form_data.append('grant_type', GRANT_TYPE.password);
    form_data.append(
      'language',
      JSON.parse(localStorage.getItem('myPagePreference') ?? '{}')?.language ??
        LANG_DEFAULT,
    );
    const res = await fetchData({
      url: '/oauth/token',
      method: 'POST',
      data: form_data,
    });

    if (res?.status === 200 || res?.data?.status === '200') {
      const resData = res?.data || res?.data?.data || {};
      tokenService.updateLocalAccessToken(resData?.access_token);
      tokenService.updateLocalCurrentUser(resData);
      setAuthenInfo({
        isLoggedIn: true,
        currentUser: resData,
        messageLogin: '',
      });
    } else {
      notification({ res });
    }
  };

  const getDictionary = async (lang: string) => {
    const res = await fetchData({
      url: '/multi-language',
      method: 'POST',
      data: {
        type: 'ui',
        languageCode: lang,
      },
    });
    if (res?.data?.status === '200') {
      const messages = [...res?.data?.data].filter(
        o => o?.languageCode === lang,
      );
      let l: any = {};
      messages?.forEach((o, i) => {
        if (o?.translation || o?.base) {
          l[`${o?.id}-${o?.base}`] = o?.translation || o?.autoTranslation;
        }
      });
      loadMessages({
        [lang]: l,
      });
      setVoca(l);
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      history.push(router.home);
    }
  }, [isLoggedIn]);

  return (
    <div className="login-main">
      <div className="login-container">
        <div className="content">
          <div className="left">
            <div className="title">
              {t('Visibility, Efficiency, Sustainability')}
              <div className="sub-title">{t('ONE PRODUCT OF DOOSOUN-CNI')}</div>
            </div>
          </div>
          <div className="right">
            <div className="right-header">
              <div className="right-logo">
                <img src={logo} alt={APP_NAME} width={32} />
                <div>{APP_NAME}</div>
              </div>
              <LangIcons />
            </div>
            <div className="login-form">
              <div className="form-container">
                <div className="header">
                  <div className="sing-in">{t('Sign In')}</div>
                  <div className="sub-sing-in">
                    {t('Welcome back! Please enter your details.')}
                  </div>
                </div>
                <div className="form-content-input">
                  <Form
                    formData={formData}
                    ref={formRef}
                    showColonAfterLabel={false}
                    className="form-login"
                    scrollingEnabled={false}
                    focusStateEnabled={false}
                    labelLocation="top"
                    items={[
                      {
                        dataField: 'loginId',
                        editorType: 'dxTextBox',
                        label: { text: t('User Name') },
                        editorOptions: {
                          onEnterKey: () => handleSubmit(),
                        },
                        isRequired: true,
                      },
                      {
                        dataField: 'password',
                        editorType: 'dxTextBox',
                        label: { text: t('Password') },
                        editorOptions: {
                          onEnterKey: () => handleSubmit(),
                          mode: viewPassword?.password ? 'text' : 'password',
                          buttons: [
                            {
                              name: 'view',
                              location: 'after',
                              options: {
                                stylingMode: 'text',
                                icon: viewPassword?.password ? eye : eyeSlash,
                                onClick: () =>
                                  setViewPassword(preState => ({
                                    ...preState,
                                    password: !preState.password,
                                  })),
                              },
                            },
                          ],
                        },
                        isRequired: true,
                      },
                    ]}
                  />
                  <div style={{ marginTop: 20 }}>
                    <Button
                      stylingMode="contained"
                      type="default"
                      text={t('Login')}
                      style={{ marginRight: 5, width: '100%' }}
                      onClick={handleSubmit}
                    />
                  </div>
                  <div className="create-account-signup">
                    <span onClick={() => history.push('/signup')}>
                      {t('Forgot Password')}
                    </span>
                  </div>
                  <div className="create-account-signup">
                    <span>{t('Don’t have an account?')}</span>
                    <span onClick={() => history.push('/signup')}>
                      {t('Sign Up')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="footer">
              <div>{t('© 2024 Doosoun - CNI. All rights reserved')}</div>
              <div className="mail-help">
                <i className="dx-icon-email" />
                <span>{'help@snacksplanet.com'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <LoadPanel visible={loading} />
    </div>
  );
}
